
import React, { useState, useEffect } from 'react';
import { Tabs, Button, List, Avatar, Tag, Modal, message, Col, Row, Spin, Alert, Typography } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ParticipantResponse } from '../../types';
import { fetchParticipants } from './participantService';
import { useGlobalContext } from '../../store/GlobalContext';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AuditTrail from '../audittrail/audittrail';
import AddParticipantModal from './addparticipant';

const { TabPane } = Tabs;
const { Title, Text } = Typography;

// Styled Components
const ParticipantsContainer = styled.div`
  padding: 1rem;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f4f6fb;
  margin-bottom: 0.5rem;
  border: 1px solid #e0e0e0;
`;

const ParticipantDetails = styled.div`
  flex-grow: 1;
  margin-left: 1rem;
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ParticipantsPage: React.FC = () => {
  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [isAddParticipantModalVisible, setIsAddParticipantModalVisible] = useState<boolean>(false);

  const { auth, selectedEstate } = useGlobalContext(); 

  const estateId = selectedEstate ? selectedEstate.uuid : '';

  const navigate = useNavigate();

  // Fetch participants when component mounts
  useEffect(() => {
    const getParticipants = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('accessToken') || Cookies.get('token'); // Adjust based on your token storage
        if (!token) {
          setError('Unauthorized. Please log in.');
          setLoading(false);
          return;
        }

        const data = await fetchParticipants(token, estateId, 'withEstates=true');
        setParticipants(data);
      } catch (err: any) {
        console.error(err);
        if (axios.isAxiosError(err)) {
          setError(err.message || 'Failed to fetch participants.');
        } else {
          setError('Failed to fetch participants.');
        }
      } finally {
        setLoading(false);
      }
    };

    getParticipants();
  }, []);

  // Handle Adding a Participant
  const handleAddParticipant = () => {
    setIsAddParticipantModalVisible(true);
  };

  // Handle Success from AddParticipantModal
  const handleSuccess = (newParticipant: ParticipantResponse) => {
    setParticipants((prev) => [...prev, newParticipant]);
    message.success('Participant added successfully.');
  };

  return (
    <Row gutter={16} style={{ marginTop: '24px' }}>
      {/* Participants and Actions */}
      <Col span={16}>
        <ParticipantsContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Title level={4}>Participants</Title>
            
          </div>

          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          <Spin spinning={loading}>
            <Tabs defaultActiveKey="1">
              <TabPane tab="Participants" key="1">
                <List
                  dataSource={participants}
                  renderItem={(participant) => (
                    <ParticipantItem key={participant.uid}>
                      <Avatar src={participant.profilePic ? participant.profilePic : `https://i.pravatar.cc/40?u=${participant.uid}`} />
                      <ParticipantDetails>
                        <ParticipantHeader>
                          <Text strong>{`${participant.prefix || ''} ${participant.firstName || ''} ${participant.middleName || ''} ${participant.lastName || ''}`.trim() || 'Unnamed Participant'}</Text>
                        </ParticipantHeader>
                        <Text type="secondary">{participant.email}</Text>
                        <div style={{ marginTop: '0.5rem' }}>
                          {participant.estates.map((estate) => (
                            <Tag color="blue" key={estate.uuid}>
                              {estate.estateName}
                            </Tag>
                          ))}
                        </div>
                      </ParticipantDetails>
                      {/* No ParticipantActions as per user requirement */}
                    </ParticipantItem>
                  )}
                />
              </TabPane>
            </Tabs>
          </Spin>
        </ParticipantsContainer>
      </Col>

      {/* Audit Trail or Other Components */}
      <Col span={8}>
        <AuditTrail />
      </Col>

      {/* Add Participant Modal */}
      {/* <AddParticipantModal
        visible={isAddParticipantModalVisible}
        onCancel={() => setIsAddParticipantModalVisible(false)}
        onSuccess={handleSuccess}
        // estateId is not required here as participants are not estate-specific
      /> */}
    </Row>
  );
};

export default ParticipantsPage;
